import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Button,
} from "@mui/material";
import CustomTextField from "../../components/common/Textfield";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PhoneNumberField from "../../components/common/PhoneNumberField";

interface CompanyFormData {
  company_name: string;
  company_type: string;
  is_active: boolean;
  description: string;

  phone_number: string;
  primary_email: string;
  secondary_email: string;
  website: string;

  street_address_1: string;
  street_address_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;

  latitude: string;
  longitude: string;
  time_zone: string;

  license_number: string;
  service_specialties: string;
  tenant_subscription: string;

  logo: File | null;
}
interface CompanyCreateResponse {
  id: number;
  name: string;
  company_type?: string;
  // add other fields if needed
}

const AddCompanyForm: React.FC = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [formData, setFormData] = useState<CompanyFormData>({
    company_name: "",
    company_type: "",
    is_active: true,
    description: "",

    phone_number: "",
    primary_email: "",
    secondary_email: "",
    website: "",

    street_address_1: "",
    street_address_2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",

    latitude: "-17.7798",
    longitude: "31.0573",
    time_zone: "Africa/Harare",

    license_number: "",
    service_specialties: "",
    tenant_subscription: "",

    logo: null as File | null,
  });

  const handleChange = <K extends keyof CompanyFormData>(
    field: K,
    value: CompanyFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const handleSubmit = async () => {
    console.log("Form submission started");
    console.log("Form Data:", formData);
    try {
      const payload = new FormData();

      // Map formData fields to backend keys
      payload.append("name", formData.company_name);
      payload.append("company_type", formData.company_type);
      payload.append("is_active", formData.is_active.toString());
      payload.append("description", formData.description || "");

      payload.append("phone_number", formData.phone_number || "");
      payload.append("primary_email", formData.primary_email || "");
      payload.append("secondary_email", formData.secondary_email || "");
      payload.append("website", formData.website || "");

      payload.append("street_address", formData.street_address_1 || "");
      payload.append("billing_address", formData.street_address_2 || "");
      payload.append("city", formData.city || "");
      payload.append("state", formData.state || "");
      payload.append("postal_code", formData.postal_code || "");
      payload.append("country", formData.country || "");

      payload.append("latitude", formData.latitude || "-17.7798");
      payload.append("longitude", formData.longitude || "31.0573");
      payload.append("time_zone", formData.time_zone || "Africa/Harare");

      payload.append("license_number", formData.license_number || "");
      payload.append("specialties", formData.service_specialties || "");

      if (formData.logo) {
        payload.append("company_logo", formData.logo);
      }
      if (formData.logo) {
        console.log("Logo file size:", formData.logo.size, "bytes");
      }
      // <-- match backend key
      console.log("FormData contents:");
      payload.forEach((value, key) => {
        console.log(key, value);
      });

      console.log("About to send POST request to backend...");

      // Use your ApiService to send the POST request
      const response = await apiService.post<CompanyCreateResponse>(
        "api/companies/company/full-create/",
        payload,
      );

      console.log("Company created:", response.data);
      toast("Company created successfully!");

      const createdCompanyId = response.data?.id;

      if (createdCompanyId) {
        // Redirect to the company view page
        navigate(`/companies/${createdCompanyId}`);
      } else {
        toast("Company created, but no ID returned. Cannot navigate.");
      }
    } catch (err: unknown) {
      console.error("Error creating company:", err);
      toast("Failed to create company. Check console for details.");
    }
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: reduxTheme.primary_colors?.[300] || "#ddd" },
      "&:hover fieldset": {
        borderColor: reduxTheme.primary_colors?.[300] || "#bbb",
      },
      "&.Mui-focused fieldset": {
        borderColor: reduxTheme.primary_colors?.[400] || "#b8865b",
      },
    },
    "& .MuiInputLabel-root": {
      color: reduxTheme.primary_colors?.[300] || "#666",
    },
    "& .MuiInputBase-input": {
      color: reduxTheme.neutral_colors?.[100] || "#333",
    },
  };
  return (
    <Box
      sx={{
        p: 4,
        maxWidth: 1200,
        borderRadius: 3,
        boxShadow: 0,
        background: reduxTheme.neutral_colors?.[900] || "#fff",
      }}
    >
      <Typography
        variant="h4"
        fontWeight={600}
        gutterBottom
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Add Company
      </Typography>

      {/* Basic Info */}
      <Typography
        variant="h6"
        sx={{ mt: 3, color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Company Details
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Company Name"
            fullWidth
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Company Type"
            fullWidth
            value={formData.company_type}
            onChange={(e) => handleChange("company_type", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label="Is Active"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Contact Information */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Contact Information
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PhoneNumberField
            defaultCountry="ZW"
            value={formData.phone_number}
            onChange={(phone: string) => handleChange("phone_number", phone)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Primary Email Address"
            fullWidth
            value={formData.primary_email}
            onChange={(e) => handleChange("primary_email", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Secondary Email Address"
            fullWidth
            value={formData.secondary_email}
            onChange={(e) => handleChange("secondary_email", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Website"
            fullWidth
            value={formData.website}
            onChange={(e) => handleChange("website", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Address */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Address & Location
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Street Address 1"
            fullWidth
            value={formData.street_address_1}
            onChange={(e) => handleChange("street_address_1", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Street Address 2"
            fullWidth
            value={formData.street_address_2}
            onChange={(e) => handleChange("street_address_2", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="City"
            fullWidth
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="State/Province"
            fullWidth
            value={formData.state}
            onChange={(e) => handleChange("state", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="Postal Code"
            fullWidth
            value={formData.postal_code}
            onChange={(e) => handleChange("postal_code", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Country"
            fullWidth
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            label="Latitude"
            fullWidth
            value="-17.7798"
            disabled
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            label="Longitude"
            fullWidth
            value="31.0573"
            disabled
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="Time Zone"
            fullWidth
            value="Africa/Harare"
            disabled
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Business Details */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Funeral / Business Details
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="License Number"
            fullWidth
            value={formData.license_number}
            onChange={(e) => handleChange("license_number", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Service Specialties"
            fullWidth
            placeholder="e.g., Cremation, Memorial Services"
            value={formData.service_specialties}
            onChange={(e) =>
              handleChange("service_specialties", e.target.value)
            }
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Branding */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Branding
      </Typography>

      <Box mt={2}>
        <Button variant="outlined" component="label">
          Upload Logo
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files ? e.target.files[0] : null;
              handleChange("logo", file);
              if (file) {
                setLogoPreview(URL.createObjectURL(file)); // preview for new upload
              } else {
                setLogoPreview(null);
              }
            }}
          />
        </Button>

        {logoPreview && (
          <Box mt={2}>
            <Typography variant="subtitle2">Logo Preview:</Typography>
            <img
              src={logoPreview}
              alt="Logo Preview"
              style={{
                width: 150,
                height: 150,
                objectFit: "contain",
                border: "1px solid #ccc",
                borderRadius: 8,
              }}
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Submit */}
      <Button variant="contained" size="large" onClick={handleSubmit}>
        Save Company
      </Button>
    </Box>
  );
};

export default AddCompanyForm;
